import { useEffect, useMemo, useState } from 'react';
import { ClipboardPaste, X, AlertTriangle, Check, Trash2, Sparkles, Search } from 'lucide-react';
import type { GrupoDeVentaVista, ProductoVista } from '@shared/types';
import {
  apodoDesde,
  leerPedidoDeChat,
  totalLeido,
  type AliasParaLeer,
  type DudaLeida,
  type GrupoParaLeer,
  type LineaLeida,
} from '@shared/leerPedidoDeChat';
import { formatPrecio } from '@renderer/utils/formatters';
import { parsePedidoWa, type PedidoExtraido } from '@shared/parsePedidoWa';

/**
 * Sprint 90/91 — "¿Qué te pidió?": se pega el chat y el sistema propone el pedido.
 *
 * El 90% de las ventas del negocio nace en un mensaje de WhatsApp que hoy se **teclea a mano**, 50
 * a 150 veces al día. Con un ciclo de unos cuatro minutos por pedido, ahí se le va la jornada a
 * quien atiende: el techo del negocio no es la cocina.
 *
 * ## Propone, no decide
 *
 * Un pedido mal interpretado que se va a la parrilla cuesta un platillo, un cliente y una llamada
 * incómoda. Uno a medias cuesta un clic. Por eso lo reconocido se muestra **junto al pedazo del
 * chat que lo produjo** —para contrastar de un vistazo— y lo dudoso se pregunta con opciones de un
 * toque.
 *
 * ## Y aprende (Sprint 91)
 *
 * Cuando el operador resuelve a mano un *"no encontré esto"* eligiendo el producto, ese apodo queda
 * guardado: la próxima vez *"una coca grande"* se reconoce solo. Es lo que evita el desenlace fácil
 * y equivocado —decirle al negocio *"renombra tu menú para que el programa entienda"*—, que sería
 * hacer que el negocio sirva al software.
 *
 * El texto pegado viene de un tercero: es entrada no confiable. Lo único que sale de aquí son
 * **productos del catálogo, con precio del catálogo**.
 *
 * ## Sprint 98 — Se pega UNA vez
 *
 * Hasta el 97 había dos "pegar el mensaje" en el mismo camino: éste, que arma el pedido, y otro en
 * el paso de los datos del cliente que **listaba** los productos y le devolvía el trabajo al
 * operador —decía, literalmente, *"estos productos los agregarás al carrito desde el menú"*—.
 * Reporte del dueño del producto, y tenía razón: *"¿y entonces para qué fue el primer modal?"*.
 *
 * Ahora este modal lee el mensaje completo: los **productos** (con su intérprete, sus dudas y sus
 * apodos) y, si se le pide, también **quién escribe y a dónde va**. El mensaje del cliente trae las
 * dos cosas revueltas; separarlas en dos pantallas era problema nuestro, no suyo.
 */
export function PegarPedidoModal({
  abierto,
  productos,
  monedaSimbolo,
  leerDatosDelCliente = false,
  onCerrar,
  onAgregar,
}: {
  abierto: boolean;
  /** El menú con su stock ya calculado por el POS: sirve para avisar de lo agotado. */
  productos: ProductoVista[];
  monedaSimbolo: string;
  /**
   * ¿Sacar también nombre, teléfono y dirección del mismo texto?
   *
   * Sólo al **empezar** un pedido. Cuando se usa desde la barra del menú para agregar algo a un
   * pedido en curso, el cliente ya está capturado: releerlo ahí sólo podría pisar lo que el
   * operador corrigió a mano.
   */
  leerDatosDelCliente?: boolean;
  onCerrar: () => void;
  onAgregar: (
    lineas: Array<{ productoId: number; nombre: string; cantidad: number }>,
    datosCliente?: PedidoExtraido | null,
  ) => void;
}) {
  const [texto, setTexto] = useState('');
  const [resueltas, setResueltas] = useState<Record<number, LineaLeida | 'descartada'>>({});
  const [quitadas, setQuitadas] = useState<Set<number>>(new Set());
  const [grupos, setGrupos] = useState<GrupoDeVentaVista[]>([]);
  const [apodos, setApodos] = useState<AliasParaLeer[]>([]);
  const [aprendidos, setAprendidos] = useState<string[]>([]);

  /**
   * Se lee **lo que el negocio capturó** (Sprint 84), no la lista plana de productos.
   *
   * Es el arreglo de fondo del Sprint 91: en el catálogo, "Frijoles Charros 1 litro" y "1/2 litro"
   * son dos renglones, pero para el cliente son **una cosa con dos tamaños**. Interpretando por
   * renglón, cada "frijoles" salía ambiguo — una pregunta que nace de nuestra base de datos y que
   * paga quien atiende, 150 veces al día.
   */
  useEffect(() => {
    if (!abierto) return;
    void (async () => {
      const [g, a] = await Promise.all([
        window.api.productos.grupos(),
        window.api.productos.apodos(),
      ]);
      if (g.ok) setGrupos(g.data);
      if (a.ok) setApodos(a.data.map((x) => ({ texto: x.texto, productoId: x.productoId })));
    })();
  }, [abierto]);

  /** El stock lo sabe el POS, que ya lo descontó contra el carrito. */
  const agotados = useMemo(() => {
    const s = new Set<number>();
    for (const p of productos) {
      const disp = p.stockEfectivo;
      if (disp !== null && disp !== undefined && disp <= 0) s.add(p.id);
    }
    return s;
  }, [productos]);

  const menu: GrupoParaLeer[] = useMemo(
    () =>
      grupos.map((g) => ({
        nombre: g.nombre,
        unidad: g.unidad,
        formatos: g.formatos
          .filter((f) => f.habilitado)
          .map((f) => ({
            productoId: f.productoId,
            nombre: f.nombre,
            cantidad: f.cantidad,
            precio: f.precio,
            disponible: !agotados.has(f.productoId),
          })),
      })),
    [grupos, agotados],
  );

  /**
   * Sprint 98 — Los dos intérpretes dejan de ignorarse.
   *
   * El de datos (Sprint 35) corre primero y dice qué líneas se llevó como nombre, teléfono y
   * dirección; el del menú (Sprint 90) las recibe para **no preguntar por ellas**. Antes leían el
   * mismo mensaje sin saber el uno del otro y el resultado salía en pantalla: la dirección aparecía
   * arriba como leída y abajo se abrían cuatro tarjetas preguntando qué producto era cada pedazo
   * de ella.
   */
  const datosDelChat = useMemo(
    () => (texto.trim() ? parsePedidoWa(texto) : null),
    [texto],
  );

  const leido = useMemo(
    () => leerPedidoDeChat(texto, menu, apodos, datosDelChat?.lineasDeDatos ?? []),
    [texto, menu, apodos, datosDelChat],
  );

  /**
   * Lo que el mismo mensaje dice del cliente: quién es, su celular, a dónde va.
   *
   * Es el otro intérprete del sistema (Sprint 35) y trabaja sobre el mismo texto. Nada de lo que
   * saca de aquí se da por bueno: se muestra, viaja al formulario y el operador lo confirma o lo
   * corrige antes de que exista el pedido.
   */
  const datosCliente = leerDatosDelCliente ? datosDelChat : null;
  const hayDatosCliente =
    !!datosCliente &&
    !!(datosCliente.nombre || datosCliente.telefono || datosCliente.direccion || datosCliente.ubicacionUrl);

  if (!abierto) return null;

  const lineasVivas = leido.lineas.filter((_, i) => !quitadas.has(i));
  const resueltasVivas = Object.values(resueltas).filter((r): r is LineaLeida => r !== 'descartada');
  const todas = [...lineasVivas, ...resueltasVivas];
  const total = totalLeido({ lineas: todas });
  const pendientes = leido.dudas.filter((_, i) => resueltas[i] === undefined).length;

  const limpiar = () => {
    setResueltas({});
    setQuitadas(new Set());
    setAprendidos([]);
  };

  const pegar = async () => {
    try {
      const t = await navigator.clipboard.readText();
      if (t) {
        setTexto(t);
        limpiar();
      }
    } catch {
      // Sin permiso de portapapeles: el área de texto sigue ahí para pegar con Ctrl+V.
    }
  };

  /**
   * Resolver una duda. Si el operador **eligió el producto de un texto que no se reconoció**, eso
   * es una lección: se guarda como apodo. Sólo en ese caso — de un "¿qué tamaño?" no se aprende
   * nada, porque hoy pidió litro y mañana pedirá medio.
   */
  const resolver = async (i: number, duda: DudaLeida, linea: LineaLeida | 'descartada') => {
    setResueltas((p) => ({ ...p, [i]: linea }));
    if (linea === 'descartada' || duda.motivo !== 'no_reconocido') return;

    const apodo = apodoDesde(duda.texto);
    if (!apodo) return;
    const r = await window.api.productos.aprenderApodo({
      texto: duda.texto,
      productoId: linea.productoId,
    });
    if (r.ok && r.data.aprendido) {
      setAprendidos((p) => [...p, `“${r.data.texto}” = ${linea.nombre}`]);
      setApodos((p) => [...p, { texto: r.data.texto, productoId: linea.productoId }]);
    }
  };

  const confirmar = () => {
    onAgregar(
      todas.map((l) => ({ productoId: l.productoId, nombre: l.nombre, cantidad: l.cantidad })),
      datosCliente,
    );
    setTexto('');
    limpiar();
    onCerrar();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-black/50 p-4">
      <div className="card my-4 w-full max-w-2xl">
        <div className="mb-4 flex items-start justify-between gap-2">
          <div>
            <h2 className="text-xl font-bold">¿Qué te pidió?</h2>
            <p className="text-sm text-slate-500">
              Copia el mensaje del chat y pégalo aquí. Reviso lo que entendí y tú corriges.
            </p>
          </div>
          <button onClick={onCerrar} className="btn-ghost p-2" aria-label="Cerrar">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/*
          La zona de pegado es grande a propósito. Un control discreto en una barra de herramientas
          no lo usa nadie: si el atajo no se ve desde dos metros, se sigue tecleando a mano.
        */}
        <div className="space-y-2">
          <button
            type="button"
            onClick={pegar}
            className="flex min-h-[92px] w-full flex-col items-center justify-center gap-1 rounded-2xl border-4 border-dashed border-slate-300 bg-slate-50 px-4 py-4 hover:border-primary hover:bg-blue-50"
          >
            <ClipboardPaste className="h-7 w-7 text-slate-500" />
            <span className="text-base font-bold">Pegar lo que te escribió</span>
            <span className="text-xs text-slate-500">O escríbelo abajo — también sirve Ctrl+V</span>
          </button>

          <textarea
            className="input min-h-[96px] w-full font-mono text-sm"
            value={texto}
            onChange={(e) => {
              setTexto(e.target.value);
              limpiar();
            }}
            placeholder={'Hola buenas tardes, me mandas 2 pollos espadas\ny 4 pan de ajo porfa'}
          />
        </div>

        {texto.trim().length > 0 && (
          <div className="mt-4 space-y-3">
            {/*
              Sprint 98 — Quién escribe va ARRIBA de qué pidió.

              Nació abajo, después de la lista de productos y de las preguntas. Reporte del dueño
              del producto: *"al ser más finitos, ¿no deberían estar primero?"*. Tiene razón por dos
              motivos del piso: es lo más corto —tres renglones que se confirman de una ojeada,
              frente a una lista que puede llevar ocho productos y cuatro dudas— y con un pedido
              largo quedaba **debajo del pliegue**, así que se confirmaba sin haberlo visto nunca.

              Y hay un orden natural: primero se sabe con quién se está hablando, después qué
              quiere. Es el mismo orden de la ventana que hay detrás.
            */}
            {hayDatosCliente && (
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
                <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-blue-700">
                  <Check className="h-4 w-4" />
                  También leí quién es
                </div>
                <ul className="space-y-0.5 text-sm text-slate-800">
                  {datosCliente?.nombre && (
                    <li>
                      <span className="text-slate-500">Nombre: </span>
                      {datosCliente.nombre}
                    </li>
                  )}
                  {datosCliente?.telefono && (
                    <li>
                      <span className="text-slate-500">Celular: </span>
                      {datosCliente.telefono}
                    </li>
                  )}
                  {datosCliente?.direccion && (
                    <li className="break-words">
                      <span className="text-slate-500">Dirección: </span>
                      {datosCliente.direccion}
                    </li>
                  )}
                  {datosCliente?.ubicacionUrl && (
                    <li className="truncate">
                      <span className="text-slate-500">Ubicación: </span>
                      {datosCliente.latitud
                        ? `punto en el mapa (${datosCliente.latitud.toFixed(5)}, ${datosCliente.longitud!.toFixed(5)})`
                        : 'enlace de ubicación'}
                    </li>
                  )}
                </ul>
                <p className="mt-1 text-xs text-blue-800">
                  Se llena en la ventana de atrás. Revísalo antes de continuar.
                </p>
              </div>
            )}

            {todas.length > 0 && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-emerald-700">
                  <Check className="h-4 w-4" />
                  Esto entendí — corrige lo que quieras
                </div>
                <ul className="space-y-1">
                  {leido.lineas.map((l, i) =>
                    quitadas.has(i) ? null : (
                      <li
                        key={`l-${i}`}
                        className="flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 text-sm"
                      >
                        <span className="min-w-0">
                          <b className="text-primary">{l.cantidad}</b> × {l.nombre}
                          {l.agotado && (
                            <span className="ml-2 rounded bg-rose-100 px-1.5 py-0.5 text-xs font-semibold text-rose-700">
                              se acabó — ofrécele otra cosa
                            </span>
                          )}
                          {/* Se dice cuándo salió de un apodo: nadie confía en lo que no entiende. */}
                          {l.porApodo && (
                            <span className="ml-2 inline-flex items-center gap-1 rounded bg-violet-100 px-1.5 py-0.5 text-xs font-semibold text-violet-700">
                              <Sparkles className="h-3 w-3" />
                              apodo aprendido
                            </span>
                          )}
                          <span className="block truncate text-xs text-slate-400">“{l.origen}”</span>
                        </span>
                        <span className="flex shrink-0 items-center gap-2">
                          <span className="font-mono font-semibold">
                            {formatPrecio(l.precio * l.cantidad, monedaSimbolo)}
                          </span>
                          <button
                            onClick={() => setQuitadas((p) => new Set(p).add(i))}
                            className="btn-ghost p-1 text-rose-700"
                            aria-label="Quitar renglón"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </span>
                      </li>
                    ),
                  )}
                  {Object.entries(resueltas).map(([k, r]) =>
                    r === 'descartada' ? null : (
                      <li
                        key={`r-${k}`}
                        className="flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 text-sm"
                      >
                        <span>
                          <b className="text-primary">{r.cantidad}</b> × {r.nombre}
                        </span>
                        <span className="font-mono font-semibold">
                          {formatPrecio(r.precio * r.cantidad, monedaSimbolo)}
                        </span>
                      </li>
                    ),
                  )}
                </ul>
              </div>
            )}

            {/*
              Las preguntas del negocio —de qué tamaño, cuántos— van una por una: cada una es una
              decisión distinta y se contesta de un toque.
            */}
            {leido.dudas.map((d, i) =>
              resueltas[i] !== undefined || d.motivo === 'no_reconocido' ? null : (
                <Duda
                  key={`d-${i}`}
                  duda={d}
                  menu={menu}
                  monedaSimbolo={monedaSimbolo}
                  onResolver={(l) => void resolver(i, d, l)}
                />
              ),
            )}

            {/*
              Sprint 98 — Lo que no se reconoció, en UNA sola tarjeta.

              Cada texto sin reconocer abría su propia tarjeta con su buscador y su parrilla de seis
              productos. Con cinco textos, la ventana eran cinco muros amarillos idénticos y el
              pedido de verdad quedaba arriba, fuera de la pantalla. Ahora es una tarjeta con la
              lista: se ve de un golpe cuántas cosas quedaron y se resuelven de una en una.
            */}
            <NoEncontradas
              dudas={leido.dudas
                .map((d, i) => ({ d, i }))
                .filter(({ d, i }) => d.motivo === 'no_reconocido' && resueltas[i] === undefined)}
              menu={menu}
              monedaSimbolo={monedaSimbolo}
              onResolver={(i, d, l) => void resolver(i, d, l)}
            />

            {aprendidos.length > 0 && (
              <div className="rounded-xl border border-violet-200 bg-violet-50 p-3 text-sm text-violet-900">
                <div className="flex items-center gap-2 font-semibold">
                  <Sparkles className="h-4 w-4" />
                  Aprendido para la próxima
                </div>
                <ul className="mt-1 space-y-0.5 text-xs">
                  {aprendidos.map((a, k) => (
                    <li key={k}>· {a}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
              <span className="text-sm font-semibold">Suma lo reconocido</span>
              <span className="font-mono text-xl font-bold text-primary">
                {formatPrecio(total, monedaSimbolo)}
              </span>
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button onClick={onCerrar} className="btn-ghost">
            Cancelar
          </button>
          {/*
            Sprint 98 — Un mensaje que sólo trae "soy Pepe, mándame algo a Júpiter 206" no arma
            pedido, pero sí trae media captura hecha. Antes el botón quedaba muerto y ese trabajo
            se perdía.
          */}
          <button
            onClick={confirmar}
            className="btn-primary min-h-[44px]"
            disabled={todas.length === 0 && !hayDatosCliente}
          >
            {todas.length === 0
              ? 'Usar sus datos y seguir'
              : pendientes > 0
                ? `Agregar ${todas.length} y seguir (${pendientes} sin resolver)`
                : `Agregar ${todas.length} al pedido`}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Sprint 98 — Todo lo que no se reconoció, junto.
 *
 * Reporte del dueño del producto sobre una captura con cinco tarjetas idénticas: *"todo lo que no
 * encontró el parser lo pone separado… o lo consolidamos en uno solo"*. Tenía razón: cinco muros
 * amarillos con la misma parrilla de seis productos empujaban el pedido de verdad fuera de la
 * pantalla, y ninguno de los cinco era comida — eran pedazos del domicilio del cliente. Eso último
 * se arregló de raíz (los dos intérpretes ya se hablan); esto es para cuando **de verdad** quede
 * algo sin reconocer: *"y algo de tomar"*, *"lo de siempre"*.
 *
 * Se abre de una en una: contestar cinco buscadores a la vez no lo hace nadie, y lo que importa es
 * que el operador vea de un golpe **cuántas** cosas quedaron pendientes y pueda seguir sin ellas.
 */
function NoEncontradas({
  dudas,
  menu,
  monedaSimbolo,
  onResolver,
}: {
  dudas: Array<{ d: DudaLeida; i: number }>;
  menu: GrupoParaLeer[];
  monedaSimbolo: string;
  onResolver: (i: number, d: DudaLeida, l: LineaLeida | 'descartada') => void;
}) {
  const [abierta, setAbierta] = useState(0);
  if (dudas.length === 0) return null;

  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-amber-900">
            {dudas.length === 1
              ? 'Hay algo que no encontré en tu menú'
              : `Hay ${dudas.length} cosas que no encontré en tu menú`}
          </div>
          <p className="text-xs text-amber-800">
            Dime qué son y me las aprendo, o sigue sin ellas: el resto del pedido ya está listo.
          </p>

          <div className="mt-2 space-y-1.5">
            {dudas.map(({ d, i }, orden) => (
              <div key={`ne-${i}`} className="rounded-lg border border-amber-300 bg-white">
                <div className="flex items-center gap-2 px-2 py-1.5">
                  <button
                    type="button"
                    onClick={() => setAbierta(orden === abierta ? -1 : orden)}
                    className="min-h-[36px] flex-1 truncate text-left text-sm font-medium text-amber-900"
                  >
                    “{d.texto.trim()}”
                  </button>
                  <button
                    type="button"
                    onClick={() => onResolver(i, d, 'descartada')}
                    className="shrink-0 rounded-lg border border-slate-300 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
                  >
                    No es del pedido
                  </button>
                </div>
                {orden === abierta && (
                  <div className="border-t border-amber-200 px-2 py-2">
                    <BuscadorDelMenu
                      menu={menu}
                      monedaSimbolo={monedaSimbolo}
                      onElegir={(f) =>
                        onResolver(i, d, {
                          productoId: f.productoId,
                          nombre: f.nombre,
                          cantidad: 1,
                          precio: f.precio,
                          origen: d.texto,
                        })
                      }
                    />
                    <p className="mt-1.5 text-xs text-amber-800">
                      Si eliges uno, me lo aprendo: la próxima vez que te escriban “
                      {d.texto.trim()}” ya voy a saber qué es.
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * El buscador del menú con sus primeras sugerencias.
 *
 * Es la pieza que hace posible aprender: sin un lugar donde decir *"esto es la coca de 3 litros"*,
 * la única salida sería capturarlo aparte — y el sistema volvería a no entenderlo mañana, y pasado,
 * y toda la vida.
 */
function BuscadorDelMenu({
  menu,
  monedaSimbolo,
  onElegir,
}: {
  menu: GrupoParaLeer[];
  monedaSimbolo: string;
  onElegir: (f: { productoId: number; nombre: string; precio: number }) => void;
}) {
  const [busqueda, setBusqueda] = useState('');
  const resultados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    const todos = menu.flatMap((g) => g.formatos);
    if (!q) return todos.slice(0, 6);
    return todos.filter((f) => f.nombre.toLowerCase().includes(q)).slice(0, 8);
  }, [busqueda, menu]);

  return (
    <>
      <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-white px-2">
        <Search className="h-4 w-4 shrink-0 text-slate-400" />
        <input
          className="min-h-[44px] w-full border-0 bg-transparent text-sm outline-none"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Busca a qué se refiere…"
          autoFocus
        />
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {resultados.map((f) => (
          <button
            key={f.productoId}
            type="button"
            onClick={() => onElegir(f)}
            className="min-h-[44px] rounded-lg border border-amber-400 bg-white px-3 text-sm font-semibold text-amber-900 hover:bg-amber-100"
          >
            {f.nombre}
            <span className="ml-1 font-normal text-amber-700">
              {formatPrecio(f.precio, monedaSimbolo)}
            </span>
          </button>
        ))}
      </div>
    </>
  );
}

/** Una pregunta concreta con su respuesta a un toque. */
function Duda({
  duda,
  menu,
  monedaSimbolo,
  onResolver,
}: {
  duda: DudaLeida;
  menu: GrupoParaLeer[];
  monedaSimbolo: string;
  onResolver: (l: LineaLeida | 'descartada') => void;
}) {
  const [busqueda, setBusqueda] = useState('');

  const titulo =
    duda.motivo === 'que_tamano'
      ? `${duda.producto} — ¿de qué tamaño?`
      : duda.motivo === 'sin_cantidad'
        ? `${duda.producto} — ¿cuántos?`
        : 'No encontré esto en tu menú';

  /**
   * Para lo no reconocido, un buscador. Es la pieza que hace posible aprender: sin un lugar donde
   * decir *"esto es la coca de 3 litros"*, la única salida sería capturarlo aparte — y el sistema
   * volvería a no entenderlo mañana, y pasado, y toda la vida.
   */
  const resultados = useMemo(() => {
    if (duda.motivo !== 'no_reconocido') return [];
    const q = busqueda.trim().toLowerCase();
    const todos = menu.flatMap((g) => g.formatos);
    if (!q) return todos.slice(0, 6);
    return todos.filter((f) => f.nombre.toLowerCase().includes(q)).slice(0, 8);
  }, [busqueda, menu, duda.motivo]);

  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-amber-900">{titulo}</div>
          {/* Tal cual lo escribió el cliente: es la única forma de que el operador decida bien. */}
          <div className="truncate text-sm text-amber-800">“{duda.texto}”</div>

          {duda.motivo === 'no_reconocido' ? (
            <div className="mt-2">
              <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-white px-2">
                <Search className="h-4 w-4 shrink-0 text-slate-400" />
                <input
                  className="min-h-[44px] w-full border-0 bg-transparent text-sm outline-none"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Busca a qué se refiere…"
                />
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {resultados.map((f) => (
                  <button
                    key={f.productoId}
                    onClick={() =>
                      onResolver({
                        productoId: f.productoId,
                        nombre: f.nombre,
                        cantidad: 1,
                        precio: f.precio,
                        origen: duda.texto,
                      })
                    }
                    className="min-h-[44px] rounded-lg border border-amber-400 bg-white px-3 text-sm font-semibold text-amber-900 hover:bg-amber-100"
                  >
                    {f.nombre}
                    <span className="ml-1 font-normal text-amber-700">
                      {formatPrecio(f.precio, monedaSimbolo)}
                    </span>
                  </button>
                ))}
                <button
                  onClick={() => onResolver('descartada')}
                  className="min-h-[44px] rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-600 hover:bg-slate-50"
                >
                  Lo capturo aparte
                </button>
              </div>
              <p className="mt-1.5 text-xs text-amber-800">
                Si eliges uno, me lo aprendo: la próxima vez que te escriban “{duda.texto.trim()}”
                ya voy a saber qué es.
              </p>
            </div>
          ) : (
            <div className="mt-2 flex flex-wrap gap-2">
              {duda.opciones.map((o, k) => (
                <button
                  key={k}
                  onClick={() =>
                    onResolver({
                      productoId: o.productoId,
                      nombre: o.nombre,
                      cantidad: o.cantidad,
                      precio: o.precio,
                      origen: duda.texto,
                      ...(o.agotado ? { agotado: true } : {}),
                    })
                  }
                  className={`min-h-[44px] rounded-lg border px-3 text-sm font-semibold ${
                    o.agotado
                      ? 'border-rose-300 bg-white text-rose-700 line-through'
                      : 'border-amber-400 bg-white text-amber-900 hover:bg-amber-100'
                  }`}
                  title={o.agotado ? 'Se acabó: mejor ofrécele otra cosa' : undefined}
                >
                  {duda.motivo === 'sin_cantidad' ? `${o.cantidad} ×` : ''} {o.nombre}
                  <span className="ml-1 font-normal opacity-80">
                    {formatPrecio(o.precio * o.cantidad, monedaSimbolo)}
                  </span>
                </button>
              ))}
              <button
                onClick={() => onResolver('descartada')}
                className="min-h-[44px] rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Quitarlo
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
